import { useState } from "react";
import { reportsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { BarChart3, Download, FileText, Receipt, FileCheck, Loader2, FileSpreadsheet, Database } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportsPage = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    document_type: "all"
  });

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const response = await reportsApi.generate(filters);
      setReportData(response.data);
      toast.success("Reporte generado exitosamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar reporte");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const handleDownloadCSV = () => {
    if (!reportData) return;

    let csvContent = "data:text/csv;charset=utf-8,";
    
    // Add invoices
    if (reportData.invoices.length > 0) {
      csvContent += "FACTURAS\n";
      csvContent += "Folio,Cliente,RFC,Total,Estado,Fecha\n";
      reportData.invoices.forEach(inv => {
        csvContent += `${inv.series}-${inv.folio},"${inv.client_name}",${inv.client_rfc},${inv.total},${inv.status},${inv.created_at}\n`;
      });
      csvContent += "\n";
    }

    // Add credit notes
    if (reportData.credit_notes.length > 0) {
      csvContent += "NOTAS DE CREDITO\n";
      csvContent += "Folio,Total,Motivo,Estado,Fecha\n";
      reportData.credit_notes.forEach(cn => {
        csvContent += `${cn.series}-${cn.folio},${cn.total},"${cn.reason}",${cn.status},${cn.created_at}\n`;
      });
      csvContent += "\n";
    }

    // Add payment complements
    if (reportData.payment_complements.length > 0) {
      csvContent += "COMPLEMENTOS DE PAGO\n";
      csvContent += "Folio,Monto,Fecha Pago,Estado,Fecha Creacion\n";
      reportData.payment_complements.forEach(pc => {
        csvContent += `${pc.series}-${pc.folio},${pc.amount},${pc.payment_date},${pc.status},${pc.created_at}\n`;
      });
    }

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `reporte_${filters.start_date}_${filters.end_date}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success("Reporte descargado");
  };

  const handleDownloadExcel = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      const url = `${API}/reports/export-excel?start_date=${filters.start_date}&end_date=${filters.end_date}&document_type=${filters.document_type}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Error al descargar");
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `reporte_${filters.start_date}_${filters.end_date}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Excel descargado (formato CSV compatible con Excel)");
    } catch (error) {
      toast.error("Error al descargar el archivo");
    }
  };

  const handleDownloadContpaqi = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      const response = await fetch(`${API}/exports/contpaqi`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          start_date: filters.start_date,
          end_date: filters.end_date,
          format: "contpaqi"
        })
      });
      
      if (!response.ok) throw new Error("Error al exportar");
      
      const data = await response.json();
      
      const blob = new Blob([data.content], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = data.filename || `polizas_contpaqi_${filters.start_date}_${filters.end_date}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Archivo CONTPAQi descargado");
    } catch (error) {
      toast.error("Error al descargar formato CONTPAQi");
    }
  };

  const handleDownloadAspel = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      const response = await fetch(`${API}/exports/contpaqi`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          start_date: filters.start_date,
          end_date: filters.end_date,
          format: "aspel"
        })
      });
      
      if (!response.ok) throw new Error("Error al exportar");
      
      const data = await response.json();
      
      const blob = new Blob([data.content], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = data.filename || `polizas_aspel_${filters.start_date}_${filters.end_date}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Archivo Aspel descargado");
    } catch (error) {
      toast.error("Error al descargar formato Aspel");
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Reportes</h1>
        <p className="text-slate-600">Genera reportes de tus documentos fiscales</p>
      </div>

      {/* Filters */}
      <Card className="cia-card">
        <CardHeader>
          <CardTitle className="text-lg">Filtros del Reporte</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Fecha Inicio</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="cia-input"
                data-testid="start-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha Fin</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="cia-input"
                data-testid="end-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo de Documento</Label>
              <Select 
                value={filters.document_type} 
                onValueChange={(value) => setFilters({ ...filters, document_type: value })}
              >
                <SelectTrigger data-testid="document-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="invoices">Solo Facturas</SelectItem>
                  <SelectItem value="credit_notes">Solo Notas de Crédito</SelectItem>
                  <SelectItem value="payment_complements">Solo Complementos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                onClick={handleGenerateReport} 
                className="w-full cia-btn-primary"
                disabled={loading}
                data-testid="generate-report-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <BarChart3 className="w-5 h-5 mr-2" />
                    Generar Reporte
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {reportData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="metric-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="metric-label">Facturas</p>
                  <p className="metric-value">{reportData.summary.total_invoices}</p>
                </div>
              </div>
            </Card>

            <Card className="metric-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="metric-label">Notas de Crédito</p>
                  <p className="metric-value">{reportData.summary.total_credit_notes}</p>
                </div>
              </div>
            </Card>

            <Card className="metric-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <FileCheck className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="metric-label">Complementos</p>
                  <p className="metric-value">{reportData.summary.total_payment_complements}</p>
                </div>
              </div>
            </Card>

            <Card className="metric-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="metric-label">Total Facturado</p>
                  <p className="text-xl font-bold text-slate-900">{formatCurrency(reportData.summary.invoices_total)}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Download Buttons */}
          <div className="flex flex-wrap justify-end gap-3">
            <Button onClick={handleDownloadCSV} variant="outline" data-testid="download-csv-btn">
              <Download className="w-5 h-5 mr-2" />
              CSV
            </Button>
            <Button onClick={handleDownloadExcel} variant="outline" data-testid="download-excel-btn">
              <FileSpreadsheet className="w-5 h-5 mr-2" />
              Excel
            </Button>
            <Button onClick={handleDownloadContpaqi} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="download-contpaqi-btn">
              <Database className="w-5 h-5 mr-2" />
              CONTPAQi
            </Button>
            <Button onClick={handleDownloadAspel} variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" data-testid="download-aspel-btn">
              <Database className="w-5 h-5 mr-2" />
              Aspel
            </Button>
          </div>

          {/* Invoices Table */}
          {reportData.invoices.length > 0 && (
            <Card className="cia-card">
              <CardHeader>
                <CardTitle className="text-lg">Facturas del Periodo</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Folio</th>
                        <th>Cliente</th>
                        <th>RFC</th>
                        <th>Total</th>
                        <th>Estado</th>
                        <th>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.invoices.map((invoice) => (
                        <tr key={invoice.id}>
                          <td className="font-medium">{invoice.series}-{invoice.folio}</td>
                          <td>{invoice.client_name}</td>
                          <td className="font-mono text-sm">{invoice.client_rfc}</td>
                          <td>{formatCurrency(invoice.total)}</td>
                          <td>
                            <span className={`badge-${invoice.status}`}>
                              {invoice.status === "draft" ? "Borrador" : invoice.status === "issued" ? "Timbrada" : "Cancelada"}
                            </span>
                          </td>
                          <td className="text-slate-500">
                            {new Date(invoice.created_at).toLocaleDateString('es-MX')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Credit Notes Table */}
          {reportData.credit_notes.length > 0 && (
            <Card className="cia-card">
              <CardHeader>
                <CardTitle className="text-lg">Notas de Crédito del Periodo</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Folio</th>
                        <th>Total</th>
                        <th>Motivo</th>
                        <th>Estado</th>
                        <th>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.credit_notes.map((note) => (
                        <tr key={note.id}>
                          <td className="font-medium">{note.series}-{note.folio}</td>
                          <td>{formatCurrency(note.total)}</td>
                          <td className="max-w-xs truncate">{note.reason}</td>
                          <td>
                            <span className={`badge-${note.status}`}>
                              {note.status === "draft" ? "Borrador" : "Timbrada"}
                            </span>
                          </td>
                          <td className="text-slate-500">
                            {new Date(note.created_at).toLocaleDateString('es-MX')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default ReportsPage;
